import React from "react";

import {
	FieldValue,
	StringFieldValue,
	updateStringFieldValue,
} from "redux/gravityForm/gravityFormSlice";
import { useDispatch, useSelector } from "redux/hooks";

import {
	FieldError,
	FormField,
	FormFieldSizeEnum,
	TextAreaField as TextAreaFieldType,
} from "types/GravityForm";

import { StyledFieldContainer, StyledTextArea } from "../GravityForm.Styled";
import { useTranslation } from "react-i18next";

interface Props {
	field: FormField & Partial<TextAreaFieldType>;
	fieldErrors: FieldError[];
	defaultValue?: string;
}

const TextAreaField = ({ field, fieldErrors, defaultValue }: Props) => {
	const { id } = field;
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const fieldValues = useSelector((state) => state.gravityForm.fieldValues);

	const fieldValue = fieldValues.find(
		(fieldValue: FieldValue) => fieldValue.id === id,
	) as StringFieldValue | undefined;

	const textAreaValue = fieldValue?.value || defaultValue;

	const updateValue = React.useCallback(
		(value: string) => {
			dispatch(updateStringFieldValue({ id, value }));
		},
		[dispatch, id],
	);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		updateValue(event.target.value);
	};

	React.useEffect(() => {
		if (textAreaValue) updateValue(textAreaValue);
	}, [textAreaValue, updateValue]);

	return (
		<StyledFieldContainer
			layoutGridColumnSpan={field.layoutGridColumnSpan}
			isError={Boolean(fieldErrors.length)}
			visibility={field.visibility}
		>
			<StyledTextArea
				label={field.label}
				required={field.isRequired}
				isError={Boolean(fieldErrors.length)}
				errorMsg={
					fieldErrors[0]?.message.includes("required")
						? t('form.validation.field.required') ?? ''
						: fieldErrors[0]?.message
				}
				onChange={handleChange}
				value={textAreaValue || ""}
				rows={
					field.size === FormFieldSizeEnum.LARGE
						? 6
						: field.size === FormFieldSizeEnum.MEDIUM
						? 4
						: 2
				}
			/>

		</StyledFieldContainer>
	);
};

export default TextAreaField;
